import React, { useState } from "react";
import * as PopoverPrimitives from "@radix-ui/react-popover";
import { ArrowsUpDownIcon } from "@heroicons/react/24/outline";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { classNames } from "components/utils";

interface SortByDropdownProps {
  sortBy: string;
  setSortBy: (sortBy: string) => void;
  hasMultipleVramOptions: boolean;
}

const SortByDropdown: React.FC<SortByDropdownProps> = ({
  sortBy,
  setSortBy,
  hasMultipleVramOptions,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const sortOptions = [
    { value: "price", label: "Price" },
    ...(hasMultipleVramOptions ? [{ value: "vram", label: "VRAM" }] : []),
    { value: "ram", label: "RAM" },
    { value: "cpu", label: "CPU" },
  ];

  const getCurrentLabel = () => {
    const option = sortOptions.find((opt) => opt.value === sortBy);
    return option?.label || "Sort by";
  };

  const handleSelect = (value: string) => {
    setSortBy(value);
    setIsOpen(false);
  };

  return (
    <PopoverPrimitives.Root open={isOpen} onOpenChange={setIsOpen}>
      <PopoverPrimitives.Trigger asChild>
        <button className="flex items-center gap-2 px-4 py-2 rounded-md border border-zinc-700 bg-zinc-800 hover:bg-zinc-700 text-white transition-colors">
          <ArrowsUpDownIcon className="w-4 h-4" />
          <span className="text-sm font-medium">
            Sort by: {getCurrentLabel()}
          </span>
        </button>
      </PopoverPrimitives.Trigger>

      <PopoverPrimitives.Portal>
        <PopoverPrimitives.Content
          side="bottom"
          align="start"
          sideOffset={8}
          className={classNames(
            "z-50 w-[250px] rounded-lg border border-zinc-700",
            "bg-zinc-900 shadow-xl",
            "animate-in fade-in-0 zoom-in-95"
          )}
        >
          <div className="p-2">
            {/* Header */}
            <div className="px-2 py-1 mb-2">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                Sort By
              </h3>
            </div>

            {/* Options */}
            <div className="space-y-1">
              {sortOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleSelect(option.value)}
                  className={classNames(
                    "w-full flex items-center justify-between px-3 py-2 rounded-md text-sm font-medium transition-all duration-200",
                    sortBy === option.value
                      ? "bg-highlight text-black"
                      : "bg-transparent text-secondary hover:bg-zinc-800 hover:text-white"
                  )}
                >
                  <span>{option.label}</span>
                  {sortBy === option.value && (
                    <CheckCircleIcon className="w-4 h-4" />
                  )}
                </button>
              ))}
            </div>
          </div>
        </PopoverPrimitives.Content>
      </PopoverPrimitives.Portal>
    </PopoverPrimitives.Root>
  );
};

export default SortByDropdown;
